/**
 * SMBIKES shared "Dein Wunschtermin" appointment component.
 *
 * Initializes every `[data-smb-termin]` element on the page: a custom date-picker
 * (Sonntag/Montag/Dienstag and past days are disabled, both visually and on submit)
 * and a time-slot <select> generated for the chosen weekday from the element's
 * `data-hours-wed-fri` / `data-hours-sat` / `data-slot-minutes` attributes (sourced
 * from the global "SMBIKES Öffnungszeiten" theme settings by the Liquid snippet).
 *
 * Each instance also self-registers a submit guard on its closest <form>, so date +
 * time are enforced as required, in-range and on an open weekday even if the hidden
 * value were tampered with directly (defense in depth on top of the visual UI).
 */
(function () {
  var CLOSED_WEEKDAYS = [0, 1, 2]; // JS Date#getDay(): 0=So, 1=Mo, 2=Di
  var MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
  var WEEKDAY_NAMES = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];

  function pad(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function toISO(date) {
    return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate());
  }

  function toDE(date) {
    return pad(date.getDate()) + '.' + pad(date.getMonth() + 1) + '.' + date.getFullYear();
  }

  function parseISO(iso) {
    var parts = (iso || '').split('-').map(Number);
    if (parts.length !== 3 || !parts[0]) return null;
    return new Date(parts[0], parts[1] - 1, parts[2]);
  }

  function parseHM(str) {
    var parts = (str || '').split(':').map(Number);
    return { h: parts[0] || 0, m: parts[1] || 0 };
  }

  function isClosedDay(date) {
    return CLOSED_WEEKDAYS.indexOf(date.getDay()) !== -1;
  }

  function startOfToday() {
    var d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function isPastDay(date, today) {
    return date.getTime() < today.getTime();
  }

  function isSelectableDay(date, today) {
    return !isPastDay(date, today) && !isClosedDay(date);
  }

  /**
   * Builds "HH:MM" slots between two "HH:MM" bounds, stepped by `stepMinutes`.
   */
  function buildSlots(openStr, closeStr, stepMinutes) {
    var open = parseHM(openStr);
    var close = parseHM(closeStr);
    var startMin = open.h * 60 + open.m;
    var endMin = close.h * 60 + close.m;
    var out = [];
    for (var m = startMin; m <= endMin; m += stepMinutes) {
      out.push(pad(Math.floor(m / 60)) + ':' + pad(m % 60));
    }
    return out;
  }

  function initInstance(root) {
    if (root.__smbTerminInit) return;
    root.__smbTerminInit = true;

    var idPrefix = root.dataset.idPrefix || '';
    var slotMinutes = parseInt(root.dataset.slotMinutes, 10) || 30;
    var hoursWedFri = (root.dataset.hoursWedFri || '09:30-18:30').split('-');
    var hoursSat = (root.dataset.hoursSat || '09:30-16:30').split('-');

    var trigger = document.getElementById(idPrefix + 'TerminDateTrigger');
    var triggerText = document.getElementById(idPrefix + 'TerminDateTriggerText');
    var hiddenDate = root.querySelector('[data-smb-termin-date]');
    var panel = document.getElementById(idPrefix + 'TerminDatePanel');
    var grid = document.getElementById(idPrefix + 'DatepickerGrid');
    var monthLabel = document.getElementById(idPrefix + 'DatepickerMonthLabel');
    var prevBtn = document.getElementById(idPrefix + 'DatepickerPrev');
    var nextBtn = document.getElementById(idPrefix + 'DatepickerNext');
    var timeSelect = root.querySelector('[data-smb-termin-time]');
    var errorEl = document.getElementById(idPrefix + 'TerminError');
    var wrap = document.getElementById(idPrefix + 'Datepicker');

    if (!trigger || !hiddenDate || !panel || !grid || !timeSelect || !wrap) return;

    var today = startOfToday();
    var viewYear = today.getFullYear();
    var viewMonth = today.getMonth();
    var selectedDate = null;

    function slotsForDate(date) {
      var weekday = date.getDay();
      if (weekday >= 3 && weekday <= 5) return buildSlots(hoursWedFri[0], hoursWedFri[1], slotMinutes);
      if (weekday === 6) return buildSlots(hoursSat[0], hoursSat[1], slotMinutes);
      return [];
    }

    function isSlotInPast(date, hm) {
      if (!date || toISO(date) !== toISO(new Date())) return false;
      var now = new Date();
      var slot = parseHM(hm);
      var slotDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), slot.h, slot.m);
      return slotDate.getTime() < now.getTime();
    }

    function resetTimeSelect(placeholder) {
      timeSelect.innerHTML = '<option value="">' + placeholder + '</option>';
      timeSelect.disabled = true;
    }

    function populateTimeSelect(date) {
      var slots = slotsForDate(date).filter(function (hm) {
        return !isSlotInPast(date, hm);
      });
      if (!slots.length) {
        resetTimeSelect('Keine Termine mehr an diesem Tag');
        return;
      }
      timeSelect.innerHTML =
        '<option value="">Bitte wählen</option>' +
        slots
          .map(function (hm) {
            return '<option value="' + hm + '">' + hm + ' Uhr</option>';
          })
          .join('');
      timeSelect.disabled = false;
    }

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message;
      errorEl.hidden = !message;
    }

    function dispatchChange() {
      var date = hiddenDate.value;
      var time = timeSelect.value;
      root.dispatchEvent(
        new CustomEvent('smbtermin:change', {
          bubbles: true,
          detail: {
            idPrefix: idPrefix,
            fieldPrefix: root.dataset.fieldPrefix,
            date: date,
            time: time,
            formattedDate: date ? toDE(parseISO(date)) : '',
            formattedTime: time ? time + ' Uhr' : ''
          }
        })
      );
    }

    function render() {
      monthLabel.textContent = MONTH_NAMES[viewMonth] + ' ' + viewYear;
      grid.innerHTML = '';

      var firstOfMonth = new Date(viewYear, viewMonth, 1);
      var leadingBlanks = (firstOfMonth.getDay() + 6) % 7; // Monday-first grid
      var daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();

      for (var i = 0; i < leadingBlanks; i++) {
        var blank = document.createElement('span');
        blank.className = 'smb-datepicker__day smb-datepicker__day--blank';
        grid.appendChild(blank);
      }

      for (var day = 1; day <= daysInMonth; day++) {
        var date = new Date(viewYear, viewMonth, day);
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'smb-datepicker__day';
        btn.textContent = day;

        if (!isSelectableDay(date, today)) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          if (isClosedDay(date)) btn.title = WEEKDAY_NAMES[date.getDay()] + ' – geschlossen';
        } else {
          btn.addEventListener(
            'click',
            (function (d) {
              return function () {
                selectDate(d);
              };
            })(date)
          );
        }

        if (selectedDate && toISO(date) === toISO(selectedDate)) {
          btn.classList.add('smb-datepicker__day--selected');
        }
        if (toISO(date) === toISO(today)) {
          btn.classList.add('smb-datepicker__day--today');
        }

        grid.appendChild(btn);
      }

      var isCurrentMonth = viewYear === today.getFullYear() && viewMonth === today.getMonth();
      prevBtn.disabled = isCurrentMonth;
    }

    function selectDate(date) {
      selectedDate = date;
      hiddenDate.value = toISO(date);
      triggerText.textContent = toDE(date);
      trigger.classList.add('smb-datepicker__trigger--filled');
      closePanel();
      populateTimeSelect(date);
      showError('');
      render();
      dispatchChange();
    }

    function openPanel() {
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      render();
      document.addEventListener('click', handleOutsideClick, true);
      document.addEventListener('keydown', handleKeydown, true);
    }

    function closePanel() {
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', handleOutsideClick, true);
      document.removeEventListener('keydown', handleKeydown, true);
    }

    function handleOutsideClick(event) {
      if (!wrap.contains(event.target)) closePanel();
    }

    function handleKeydown(event) {
      if (event.key === 'Escape') {
        closePanel();
        trigger.focus();
      }
    }

    trigger.addEventListener('click', function () {
      if (panel.hidden) openPanel();
      else closePanel();
    });

    prevBtn.addEventListener('click', function () {
      viewMonth -= 1;
      if (viewMonth < 0) {
        viewMonth = 11;
        viewYear -= 1;
      }
      render();
    });

    nextBtn.addEventListener('click', function () {
      viewMonth += 1;
      if (viewMonth > 11) {
        viewMonth = 0;
        viewYear += 1;
      }
      render();
    });

    timeSelect.addEventListener('change', function () {
      showError('');
      dispatchChange();
    });

    resetTimeSelect('Bitte zuerst ein Datum wählen');
    render();

    // Public API for pages that need to relabel the appointment contextually
    // (e.g. Hol- & Bringservice switching between "Gewünschte Abholung" and
    // "Gewünschte Fahrradabgabe") or read the current value for a summary box.
    root.smbTermin = {
      setFieldPrefix: function (newPrefix) {
        root.dataset.fieldPrefix = newPrefix;
        hiddenDate.name = 'contact[' + newPrefix + ' – Datum]';
        timeSelect.name = 'contact[' + newPrefix + ' – Uhrzeit]';
        dispatchChange();
      },
      getValue: function () {
        var date = hiddenDate.value;
        var time = timeSelect.value;
        return {
          date: date,
          time: time,
          formattedDate: date ? toDE(parseISO(date)) : '',
          formattedTime: time ? time + ' Uhr' : ''
        };
      },
      validate: function () {
        if (!hiddenDate.value) {
          showError('Bitte wähle deinen Wunschtermin aus.');
          return false;
        }
        if (!timeSelect.value) {
          showError('Bitte wähle eine Wunschzeit aus.');
          return false;
        }
        var date = parseISO(hiddenDate.value);
        if (!date) {
          showError('Bitte wähle deinen Wunschtermin aus.');
          return false;
        }
        if (!isSelectableDay(date, startOfToday())) {
          showError('Bitte wähle einen Termin Mi.–Sa. in der Zukunft (So.–Di. haben wir geschlossen).');
          return false;
        }
        if (slotsForDate(date).indexOf(timeSelect.value) === -1) {
          showError('Diese Uhrzeit liegt außerhalb unserer Öffnungszeiten. Bitte wähle eine andere Zeit.');
          return false;
        }
        if (isSlotInPast(date, timeSelect.value)) {
          showError('Dieser Termin liegt in der Vergangenheit. Bitte wähle eine spätere Uhrzeit.');
          return false;
        }
        showError('');
        return true;
      },
      focus: function () {
        trigger.focus();
      }
    };

    // Defense-in-depth safety net: always block submission if the termin is
    // invalid, even if smb-form-validate.js failed to load for some reason.
    // Scrolling/focusing the actual first invalid field across the whole
    // form (which may be an earlier field than this one) is owned by the
    // shared validator in smb-form-validate.js, so this listener does not
    // scroll/focus itself — otherwise both scripts would fight over which
    // field the page jumps to.
    var form = root.closest('form');
    if (form) {
      form.addEventListener('submit', function (event) {
        if (!root.smbTermin.validate()) {
          event.preventDefault();
        }
      });
    }
  }

  // This script tag is always placed right after the component's own markup
  // (point-of-use loading, same convention as the rest of this theme), so the
  // target elements already exist in the DOM — initialize immediately rather
  // than waiting for DOMContentLoaded, so `root.smbTermin` is guaranteed to be
  // ready before any later inline script on the same page tries to use it.
  var instances = document.querySelectorAll('[data-smb-termin]');
  instances.forEach(initInstance);
})();
